import React, { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import {
  Phone, MessageSquare, PhoneIncoming, PhoneOutgoing, PhoneMissed, ArrowLeft,
  Plus, Upload, Trash2, Loader2, CheckCircle2, BarChart3, CalendarPlus,
} from "lucide-react";
import { api } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";

const FIELD_LABELS = {
  date: "Date & time", kind: "Type (call/text)", direction: "Direction",
  duration: "Duration", status: "Status", contact: "Contact", notes: "Notes",
};

function ColumnMapDialog({ open, onOpenChange, preview, onConfirm, busy }) {
  const [map, setMap] = useState({});
  useEffect(() => { if (preview) setMap({ ...(preview.auto_map || {}) }); }, [preview]);
  if (!preview) return null;
  const fields = preview.fields || Object.keys(FIELD_LABELS);
  const NONE = "__none__";
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" style={{ background: "var(--ek-surface)", borderColor: "var(--ek-border)" }} data-testid="csv-map-dialog">
        <DialogHeader>
          <DialogTitle className="font-head font-semibold tracking-tight">Map your CSV columns</DialogTitle>
          <DialogDescription style={{ color: "var(--ek-text-secondary)" }}>
            We auto-matched these from your file. Adjust any that look wrong, then import.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-1 max-h-[52vh] overflow-y-auto">
          {fields.map((f) => (
            <div key={f} className="flex items-center justify-between gap-3">
              <Label className="w-36 shrink-0">{FIELD_LABELS[f] || f}</Label>
              <Select value={map[f] || NONE} onValueChange={(v) => setMap((m) => ({ ...m, [f]: v === NONE ? undefined : v }))}>
                <SelectTrigger className="flex-1" data-testid={`csv-map-${f}`} style={{ background: "var(--ek-page)", borderColor: "var(--ek-border)" }}>
                  <SelectValue placeholder="— Not in file —" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={NONE}>— Not in file —</SelectItem>
                  {(preview.columns || []).map((c) => (
                    <SelectItem key={c} value={c}>{c}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          ))}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-md" style={{ borderColor: "var(--ek-border)", background: "transparent" }}>Cancel</Button>
          <Button onClick={() => onConfirm(map)} disabled={busy || !map.date} data-testid="csv-map-confirm" className="rounded-md" style={{ background: "var(--ek-primary)", color: "#fff" }}>
            {busy ? "Importing…" : "Import"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function fmtTime(iso) {
  try { return new Date(iso).toLocaleString(undefined, { month: "short", day: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }); }
  catch { return iso; }
}
function fmtDuration(s) {
  if (!s) return "—";
  const m = Math.floor(s / 60), sec = s % 60;
  return `${m}m ${sec}s`;
}

function StatCard({ label, value, sub }) {
  return (
    <div className="rounded-lg border p-4" style={{ background: "var(--ek-surface)", borderColor: "var(--ek-border)" }}>
      <div className="text-2xl font-head font-bold" style={{ color: "var(--ek-primary)" }}>{value}</div>
      <div className="ek-mono-label mt-1">{label}</div>
      {sub && <div className="text-xs mt-0.5" style={{ color: "var(--ek-text-muted)" }}>{sub}</div>}
    </div>
  );
}

function LogDialog({ open, onOpenChange, matterId, onSaved }) {
  const [kind, setKind] = useState("call");
  const [direction, setDirection] = useState("incoming");
  const [status, setStatus] = useState("answered");
  const [when, setWhen] = useState("");
  const [duration, setDuration] = useState("");
  const [contact, setContact] = useState("");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);

  const reset = () => { setKind("call"); setDirection("incoming"); setStatus("answered"); setWhen(""); setDuration(""); setContact(""); setNotes(""); };

  const save = async () => {
    setBusy(true);
    try {
      await api.post("/comms", {
        matter_id: matterId, kind, direction,
        occurred_at: when ? new Date(when).toISOString() : new Date().toISOString(),
        duration_sec: kind === "call" ? Math.round((parseFloat(duration) || 0) * 60) : 0,
        status: kind === "call" ? status : (direction === "outgoing" ? "sent" : "received"),
        contact, notes,
      });
      toast.success("Communication logged");
      onSaved();
      onOpenChange(false);
      reset();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { onOpenChange(v); if (!v) reset(); }}>
      <DialogContent className="max-w-md" style={{ background: "var(--ek-surface)", borderColor: "var(--ek-border)" }} data-testid="comm-log-dialog">
        <DialogHeader>
          <DialogTitle className="font-head font-semibold tracking-tight">Log a communication</DialogTitle>
          <DialogDescription style={{ color: "var(--ek-text-secondary)" }}>Record a call or text — facts only.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-1">
          <div className="grid grid-cols-2 gap-2">
            {[["call", "Call", Phone], ["text", "Text", MessageSquare]].map(([k, l, Icon]) => (
              <button key={k} onClick={() => setKind(k)} data-testid={`comm-kind-${k}`} className="flex items-center justify-center gap-2 rounded-md border py-2 text-sm transition-colors"
                style={{ borderColor: kind === k ? "var(--ek-primary)" : "var(--ek-border)", background: kind === k ? "rgba(46,130,246,0.1)" : "transparent", color: kind === k ? "var(--ek-primary)" : "var(--ek-text-secondary)" }}>
                <Icon className="h-4 w-4" /> {l}
              </button>
            ))}
          </div>
          <div className="grid grid-cols-2 gap-2">
            {[["incoming", "Incoming"], ["outgoing", "Outgoing"]].map(([k, l]) => (
              <button key={k} onClick={() => setDirection(k)} data-testid={`comm-dir-${k}`} className="rounded-md border py-2 text-sm transition-colors"
                style={{ borderColor: direction === k ? "var(--ek-primary)" : "var(--ek-border)", background: direction === k ? "rgba(46,130,246,0.1)" : "transparent", color: direction === k ? "var(--ek-primary)" : "var(--ek-text-secondary)" }}>
                {l}
              </button>
            ))}
          </div>
          {kind === "call" && (
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Status</Label>
                <div className="flex gap-2">
                  {[["answered", "Answered"], ["missed", "Missed"]].map(([k, l]) => (
                    <button key={k} onClick={() => setStatus(k)} data-testid={`comm-status-${k}`} className="flex-1 rounded-md border py-1.5 text-xs transition-colors"
                      style={{ borderColor: status === k ? "var(--ek-primary)" : "var(--ek-border)", background: status === k ? "rgba(46,130,246,0.1)" : "transparent", color: status === k ? "var(--ek-primary)" : "var(--ek-text-secondary)" }}>
                      {l}
                    </button>
                  ))}
                </div>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="dur">Duration (min)</Label>
                <Input id="dur" data-testid="comm-duration" type="number" min="0" value={duration} onChange={(e) => setDuration(e.target.value)} className="bg-transparent" placeholder="e.g. 5" />
              </div>
            </div>
          )}
          <div className="space-y-1.5">
            <Label htmlFor="when">Date & time</Label>
            <Input id="when" data-testid="comm-when" type="datetime-local" value={when} onChange={(e) => setWhen(e.target.value)} className="bg-transparent" />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="contact">Contact (optional)</Label>
            <Input id="contact" data-testid="comm-contact" value={contact} onChange={(e) => setContact(e.target.value)} className="bg-transparent" placeholder="Name or number" />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="notes">Notes (optional)</Label>
            <Textarea id="notes" data-testid="comm-notes" value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} className="bg-transparent resize-none" placeholder="Observable facts only" />
          </div>
        </div>
        <DialogFooter>
          <Button onClick={save} disabled={busy} data-testid="comm-save-btn" className="rounded-md" style={{ background: "var(--ek-primary)", color: "#fff" }}>
            {busy ? "Saving…" : "Log communication"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function ParentChildComms({ matterId, onBack }) {
  const [stats, setStats] = useState(null);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [logOpen, setLogOpen] = useState(false);
  const [importing, setImporting] = useState(false);
  const [mapOpen, setMapOpen] = useState(false);
  const [preview, setPreview] = useState(null);
  const [pendingFile, setPendingFile] = useState(null);
  const skipRef = useRef(0);
  const fileRef = useRef(null);

  const loadStats = useCallback(async () => {
    if (!matterId) return;
    try { const { data } = await api.get(`/comms/stats?matter_id=${matterId}`); setStats(data); } catch { /* */ }
  }, [matterId]);

  const loadList = useCallback(async (reset) => {
    if (!matterId) return;
    const skip = reset ? 0 : skipRef.current;
    try {
      const { data } = await api.get(`/comms?matter_id=${matterId}&skip=${skip}&limit=30`);
      setTotal(data.total);
      setHasMore(data.has_more);
      skipRef.current = skip + data.items.length;
      setItems((prev) => (reset ? data.items : [...prev, ...data.items]));
    } catch { /* */ }
  }, [matterId]);

  const refresh = useCallback(async () => {
    setLoading(true);
    skipRef.current = 0;
    await Promise.all([loadStats(), loadList(true)]);
    setLoading(false);
  }, [loadStats, loadList]);

  useEffect(() => { refresh(); }, [refresh]);

  const remove = async (id) => {
    try { await api.delete(`/comms/${id}`); setItems((p) => p.filter((x) => x.id !== id)); loadStats(); toast.success("Entry removed"); }
    catch { toast.error("Delete failed"); }
  };

  const onImport = async (file) => {
    if (!file) return;
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("matter_id", matterId);
      const { data } = await api.post("/comms/import/preview", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPreview(data);
      setPendingFile(file);
      setMapOpen(true);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not read the CSV"); }
    finally { setImporting(false); }
  };

  const doImport = async (mapping) => {
    if (!pendingFile) return;
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append("file", pendingFile);
      fd.append("matter_id", matterId);
      fd.append("mapping", JSON.stringify(mapping || {}));
      const { data } = await api.post("/comms/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Imported ${data.imported} record${data.imported === 1 ? "" : "s"}${data.skipped ? ` · ${data.skipped} skipped` : ""}`);
      setMapOpen(false);
      setPendingFile(null);
      setPreview(null);
      refresh();
    } catch (e) { toast.error(e.response?.data?.detail || "Import failed"); }
    finally { setImporting(false); }
  };

  const addToTimeline = async (id) => {
    try {
      await api.post(`/comms/${id}/to-timeline`);
      setItems((p) => p.map((x) => (x.id === id ? { ...x, linked_incident_id: "done" } : x)));
      toast.success("Added to the timeline");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not add to timeline"); }
  };

  if (!matterId) {
    return <div className="text-center py-16 text-sm" style={{ color: "var(--ek-text-secondary)" }}>Select a case first.</div>;
  }

  const t = stats?.totals || {};
  const maxWeek = Math.max(1, ...(stats?.weekly || []).map((w) => w.calls + w.texts));

  return (
    <div className="space-y-6" data-testid="comms-module">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          {onBack && (
            <button onClick={onBack} className="h-8 w-8 rounded-md flex items-center justify-center hover:bg-white/5" data-testid="comms-back" style={{ color: "var(--ek-text-secondary)" }}>
              <ArrowLeft className="h-5 w-5" />
            </button>
          )}
          <div>
            <div className="ek-stamp inline-block mb-1">COMMUNICATION LOG</div>
            <h2 className="font-head text-2xl font-semibold tracking-tight">Parent-Child Communication</h2>
            <p className="text-sm mt-1" style={{ color: "var(--ek-text-secondary)" }}>{total} logged · calls & texts</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={() => fileRef.current?.click()} disabled={importing} variant="outline" data-testid="comms-import-btn" className="rounded-md" style={{ borderColor: "var(--ek-border)", background: "transparent" }}>
            {importing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Upload className="h-4 w-4 mr-2" style={{ color: "var(--ek-primary)" }} />} Import CSV
          </Button>
          <Button onClick={() => setLogOpen(true)} data-testid="comms-log-btn" className="rounded-md" style={{ background: "var(--ek-primary)", color: "#fff" }}>
            <Plus className="h-4 w-4 mr-2" /> Log entry
          </Button>
          <input ref={fileRef} type="file" accept=".csv,text/csv" className="hidden" data-testid="comms-csv-input" onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; onImport(f); }} />
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin" style={{ color: "var(--ek-primary)" }} /></div>
      ) : (
        <>
          {/* Stats */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="comms-stats">
            <StatCard label="Total Calls" value={t.calls || 0} sub={`${t.incoming_calls || 0} in · ${t.outgoing_calls || 0} out`} />
            <StatCard label="Answered Rate" value={`${t.answered_rate || 0}%`} sub={`${t.calls_answered || 0} answered · ${t.calls_missed || 0} missed`} />
            <StatCard label="Total Texts" value={t.texts || 0} sub={`${t.texts_sent || 0} sent · ${t.texts_received || 0} received`} />
            <StatCard label="Call Time" value={fmtDuration(t.total_call_seconds || 0)} sub="total logged" />
          </div>

          {/* Weekly activity */}
          <div className="rounded-lg border p-5" style={{ background: "var(--ek-surface)", borderColor: "var(--ek-border)" }} data-testid="comms-weekly">
            <div className="flex items-center gap-2 mb-4">
              <BarChart3 className="h-4 w-4" style={{ color: "var(--ek-primary)" }} />
              <span className="font-head font-semibold text-sm tracking-tight">Weekly activity (last 8 weeks)</span>
            </div>
            <div className="flex items-end gap-2 h-28">
              {(stats?.weekly || []).map((w, i) => {
                const h = Math.round(((w.calls + w.texts) / maxWeek) * 100);
                return (
                  <div key={i} className="flex-1 flex flex-col items-center gap-1">
                    <div className="w-full rounded-t" style={{ height: `${Math.max(4, h)}%`, background: "var(--ek-primary)", opacity: 0.85 }} title={`${w.calls} calls · ${w.texts} texts`} />
                    <span className="ek-mono-label" style={{ fontSize: "9px" }}>{w.week_start.slice(5)}</span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* List */}
          <div className="space-y-2" data-testid="comms-list">
            {items.length === 0 ? (
              <div className="border border-dashed rounded-lg p-12 text-center" style={{ borderColor: "var(--ek-border)" }}>
                <Phone className="h-8 w-8 mx-auto mb-3" style={{ color: "var(--ek-text-muted)" }} strokeWidth={1.3} />
                <p className="text-sm" style={{ color: "var(--ek-text-secondary)" }}>No communications logged yet. Log one or import a CSV phone record.</p>
              </div>
            ) : items.map((c) => {
              const isCall = c.kind === "call";
              const Icon = isCall
                ? (c.status === "missed" ? PhoneMissed : (c.direction === "incoming" ? PhoneIncoming : PhoneOutgoing))
                : MessageSquare;
              const color = c.status === "missed" ? "var(--ek-warning)" : "var(--ek-primary)";
              return (
                <div key={c.id} className="flex items-center gap-3 border rounded-lg px-4 py-3" style={{ background: "var(--ek-surface)", borderColor: "var(--ek-border)" }} data-testid={`comm-row-${c.id}`}>
                  <Icon className="h-5 w-5 shrink-0" style={{ color }} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-medium capitalize">{c.kind} · {c.direction}</span>
                      {isCall && <span className="ek-stamp" style={{ color, borderColor: "var(--ek-border)" }}>{c.status}</span>}
                      {isCall && c.duration_sec > 0 && <span className="ek-mono-label">{fmtDuration(c.duration_sec)}</span>}
                      {c.source === "import" && <span className="ek-mono-label" style={{ color: "var(--ek-text-muted)" }}>IMPORTED</span>}
                    </div>
                    <div className="text-xs mt-0.5" style={{ color: "var(--ek-text-muted)" }}>
                      {fmtTime(c.occurred_at)}{c.contact ? ` · ${c.contact}` : ""}
                    </div>
                    {c.notes && <p className="text-sm mt-1" data-private style={{ color: "var(--ek-text-secondary)" }}>{c.notes}</p>}
                  </div>
                  <div className="flex items-center gap-1.5 shrink-0">
                    {c.linked_incident_id ? (
                      <span className="ek-mono-label" style={{ color: "var(--ek-secure)" }} data-testid={`comm-linked-${c.id}`}>ON TIMELINE</span>
                    ) : (
                      <button onClick={() => addToTimeline(c.id)} data-testid={`comm-to-timeline-${c.id}`} title="Add to timeline" className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-md border transition-colors hover:border-[var(--ek-primary)]" style={{ borderColor: "var(--ek-border)", color: "var(--ek-primary)" }}>
                        <CalendarPlus className="h-3.5 w-3.5" /> Timeline
                      </button>
                    )}
                    <button onClick={() => remove(c.id)} data-testid={`comm-delete-${c.id}`} className="text-slate-500 hover:text-red-400 transition-colors p-1">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              );
            })}
            {hasMore && (
              <div className="text-center pt-2">
                <button onClick={() => loadList(false)} data-testid="comms-load-more" className="ek-mono-label" style={{ color: "var(--ek-primary)" }}>Load more</button>
              </div>
            )}
          </div>
        </>
      )}

      <LogDialog open={logOpen} onOpenChange={setLogOpen} matterId={matterId} onSaved={refresh} />
      <ColumnMapDialog open={mapOpen} onOpenChange={(v) => { setMapOpen(v); if (!v) { setPendingFile(null); setPreview(null); } }} preview={preview} onConfirm={doImport} busy={importing} />
    </div>
  );
}
